'use client';

import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  onIntersect: () => void;
  enabled?: boolean;
  rootMargin?: string;
  threshold?: number;
}

/**
 * A hook that implements infinite scrolling using Intersection Observer API
 */
export function useInfiniteScroll({
  onIntersect,
  enabled = true,
  rootMargin = '0px',
  threshold = 0.1,
}: UseInfiniteScrollOptions) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  const setTargetRef = useCallback((element: HTMLDivElement | null) => {
    targetRef.current = element;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          onIntersectRef.current();
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    // Observe target element
    const currentTarget = targetRef.current;
    if (currentTarget) {
      observerRef.current.observe(currentTarget);
    }

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, rootMargin, threshold]);

  return { setTargetRef };
}
